/* /assets/basket-builder.js */
(() => {
  'use strict';

  const STORAGE_KEY = 'BasketBuilderState';
  const SELECTORS = {
    root: '#BasketBuilderRoot',
    step: '[data-basket-builder-step]',
    variantRadios: 'input[name="bb_box_variant"]',
    continueBtn: '[data-basket-builder-continue]',
    categorySelect: '#BasketBuilderCategorySelect',
    productsGrid: '#BasketBuilderProductsGrid',
    reviewBtn: '[data-basket-builder-review]',
    reviewItems: '#BasketBuilderReviewItems',
    removeItemBtn: '[data-basket-builder-remove-item]',
    addMoreBtn: '[data-basket-builder-add-more]',
    checkoutBtn: '[data-basket-builder-checkout]',
    deliveryNotes: '#BasketBuilderDeliveryNotes',
    giftNote: '#BasketBuilderGiftNote',
    boxSummary: '#BasketBuilderBoxSummary',
    reviewMeta: '#BasketBuilderReviewMeta',
    underfillNotice: '#BasketBuilderUnderfillNotice',
    progressHeader: '#BasketBuilderProgressHeader',
    selectedBoxLabel: '#BasketBuilderSelectedBoxLabel',
    toast: '#BasketBuilderToast'
  };

  const root = document.querySelector(SELECTORS.root);
  if (!root) return;

  const state = {
    selectedVariantId: null,
    selectedCapacity: 0,
    selectedLabel: '',
    currentStep: 1,
    selectedCategory: '',
    cart: null,
    didShowCompleteToast: false
  };

  const elements = {
    steps: Array.from(root.querySelectorAll(SELECTORS.step)),
    variantRadios: Array.from(root.querySelectorAll(SELECTORS.variantRadios)),
    continueBtn: root.querySelector(SELECTORS.continueBtn),
    categorySelect: root.querySelector(SELECTORS.categorySelect),
    productsGrid: root.querySelector(SELECTORS.productsGrid),
    reviewBtn: root.querySelector(SELECTORS.reviewBtn),
    reviewItems: root.querySelector(SELECTORS.reviewItems),
    addMoreBtn: root.querySelector(SELECTORS.addMoreBtn),
    checkoutBtn: root.querySelector(SELECTORS.checkoutBtn),
    deliveryNotes: root.querySelector(SELECTORS.deliveryNotes),
    giftNote: root.querySelector(SELECTORS.giftNote),
    boxSummary: root.querySelector(SELECTORS.boxSummary),
    reviewMeta: root.querySelector(SELECTORS.reviewMeta),
    underfillNotice: root.querySelector(SELECTORS.underfillNotice),
    progressHeader: root.querySelector(SELECTORS.progressHeader),
    selectedBoxLabel: root.querySelector(SELECTORS.selectedBoxLabel),
    toast: root.querySelector(SELECTORS.toast),
    step2: root.querySelector('#BasketBuilderStep2')
  };

  let toastTimer = null;
  let modalRefs = null;
  let detailsModalRefs = null;
  let currentModalProductData = null;
  let currentDetailsProductData = null;
  let lastFocusedOpener = null;

  function readStoredState() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        state.selectedVariantId = parsed.boxVariantId || null;
        state.selectedCapacity = Number(parsed.capacity) || 0;
        state.selectedLabel = typeof parsed.label === 'string' ? parsed.label : '';
        state.currentStep = Number(parsed.currentStep) || 1;
        state.selectedCategory = typeof parsed.selectedCategory === 'string' ? parsed.selectedCategory : '';
      }
    } catch (error) {
      console.warn('[BasketBuilder] Failed to read local state:', error);
    }
  }

  function writeStoredState() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          boxVariantId: state.selectedVariantId,
          capacity: state.selectedCapacity,
          label: state.selectedLabel,
          currentStep: state.currentStep,
          selectedCategory: state.selectedCategory
        })
      );
    } catch (error) {
      console.warn('[BasketBuilder] Failed to persist local state:', error);
    }
  }

  function clearStoredState() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[BasketBuilder] Failed to clear local state:', error);
    }
  }

  function showToast(message, type = 'info', durationMs = 1800) {
    if (!elements.toast) return;

    elements.toast.textContent = message;
    elements.toast.dataset.type = type;
    elements.toast.classList.add('bb-toast--show');

    if (toastTimer) {
      window.clearTimeout(toastTimer);
    }

    toastTimer = window.setTimeout(() => {
      elements.toast.classList.remove('bb-toast--show');
    }, durationMs);
  }

  function smoothScrollTo(element) {
    if (!element || typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToBuilderTop() {
    if (!root || typeof root.scrollIntoView !== 'function') return;
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToPageTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeBoolean(value) {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return null;
  }

  function isBoxByProperties(item) {
    return !!(item && item.properties && (item.properties._bb_box === '1' || item.properties._bb_box === 1));
  }

  function getItemIsBoxValue(item) {
    if (isBoxByProperties(item)) {
      return true;
    }

    const isBoxField = item && item.product && item.product.metafields && item.product.metafields.custom
      ? item.product.metafields.custom.is_box
      : null;

    if (isBoxField == null) return false;

    const raw = typeof isBoxField === 'object' && 'value' in isBoxField
      ? isBoxField.value
      : isBoxField;

    return normalizeBoolean(raw);
  }

  function isBoxItem(item) {
    return getItemIsBoxValue(item) === true;
  }

  function getFillerCount(cart) {
    return (cart.items || []).reduce((count, item) => {
      if (isBoxByProperties(item)) {
        return count;
      }

      const isBox = getItemIsBoxValue(item);
      if (isBox !== true) {
        return count + Number(item.quantity || 0);
      }
      return count;
    }, 0);
  }

  function getBoxItem(cart) {
    return (cart.items || []).find((item) => getItemIsBoxValue(item) === true) || null;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Failed cart fetch (${response.status})`);
    }

    const cart = await response.json();
    state.cart = cart;
    return cart;
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Request failed (${response.status}) for ${url}`);
    }

    return response.json();
  }

  function updateStep2Header() {
    if (elements.selectedBoxLabel) {
      const label = state.selectedLabel ? state.selectedLabel : '-';
      elements.selectedBoxLabel.textContent = `Selected box: ${label}`;
    }
  }

  function setStepVisibility(stepNumber) {
    elements.steps.forEach((stepEl) => {
      const value = Number(stepEl.getAttribute('data-basket-builder-step'));
      const isActive = value === stepNumber;
      stepEl.hidden = !isActive;
      stepEl.classList.toggle('bb-hidden', !isActive);
    });
    state.currentStep = stepNumber;
    writeStoredState();
  }

  async function ensureStep2ProductsVisible() {
    if (!elements.productsGrid || !elements.categorySelect) return;
    const hasGridContent = elements.productsGrid.textContent.trim().length > 0;
    if (hasGridContent) return;
    if (elements.categorySelect.value) {
      await loadCategoryCollection(elements.categorySelect.value);
    }
  }

  async function goToStep(stepNumber) {
    setStepVisibility(stepNumber);

    if (stepNumber === 2) {
      updateStep2Header();
      await refreshProgress();
      await ensureStep2ProductsVisible();
    }

    if (stepNumber === 3) {
      await renderReview();
      scrollToPageTop();
    }
  }

  function setSelectedVariantFromRadio(radio, persist = true) {
    if (!radio) return;

    const capacity = Number(radio.getAttribute('data-capacity'));
    const title = radio.getAttribute('data-title') || '';

    state.selectedVariantId = radio.value || null;
    state.selectedCapacity = Number.isFinite(capacity) ? capacity : 0;
    state.selectedLabel = title;
    state.didShowCompleteToast = false;

    if (!state.selectedCapacity) {
      console.error('[BasketBuilder] Missing or invalid data-capacity on selected box variant.', {
        variantId: state.selectedVariantId,
        capacityRaw: radio.getAttribute('data-capacity')
      });
    }

    elements.variantRadios.forEach((input) => {
      const label = root.querySelector(`label[for="${input.id}"]`);
      if (!label) return;
      label.classList.toggle('is-active', input === radio);
    });

    if (elements.continueBtn) {
      elements.continueBtn.disabled = !state.selectedVariantId;
    }

    updateStep2Header();

    if (persist) {
      writeStoredState();
    }
  }

  async function removeExistingBoxItems() {
    const cart = await fetchCart();
    const boxItems = (cart.items || []).filter((item) => isBoxByProperties(item) || getItemIsBoxValue(item) === true);

    for (const item of boxItems) {
      await postJson('/cart/change.js', { id: item.key, quantity: 0 });
    }
  }

  async function handleContinue() {
    if (!state.selectedVariantId) {
      showToast('Please select a basket size first.', 'error');
      return;
    }

    if (!state.selectedCapacity || state.selectedCapacity < 1) {
      console.error('[BasketBuilder] Continue blocked due to missing capacity.', {
        selectedVariantId: state.selectedVariantId,
        selectedCapacity: state.selectedCapacity
      });
      showToast('Could not determine box capacity.', 'error');
      return;
    }

    try {
      await removeExistingBoxItems();
      await postJson('/cart/add.js', {
        items: [{
          id: Number(state.selectedVariantId),
          quantity: 1,
          properties: { _bb_box: '1' }
        }]
      });

      state.didShowCompleteToast = false;
      writeStoredState();
      showToast('Added to basket', 'success');
      await goToStep(2);
      updateStep2Header();
      await refreshProgress();
      smoothScrollTo(elements.step2);
    } catch (error) {
      console.error('[BasketBuilder] Continue failed:', error);
      showToast('Unable to add box right now.', 'error');
    }
  }

  let currentCategoryHandle = null;

  async function loadCategoryCollection(handle, page) {
    if (!elements.productsGrid || !handle) return;

    currentCategoryHandle = handle;
    elements.productsGrid.innerHTML = '<p>Loading...</p>';

    try {
      let url = `/collections/${encodeURIComponent(handle)}?section_id=basket-builder-grid`;
      if (page && page > 1) url += `&page=${page}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) {
        throw new Error(`Collection fetch failed (${response.status})`);
      }

      const html = await response.text();
      elements.productsGrid.innerHTML = html;
      bindAddButtonsInGrid();
    } catch (error) {
      console.error('[BasketBuilder] Failed to load category grid:', error);
      showToast('Could not load this category.', 'error');
    }
  }

  async function addFillerVariant(variantId) {
    if (!state.selectedCapacity || state.selectedCapacity < 1) {
      console.error('[BasketBuilder] Cannot add filler item without a valid selected capacity.');
      showToast('Select a box before adding items.', 'error');
      return false;
    }

    try {
      const cart = await fetchCart();
      const fillerCount = getFillerCount(cart);

      if (fillerCount >= state.selectedCapacity) {
        showToast('Basket full — remove an item to add another.', 'warning');
        return false;
      }

      await postJson('/cart/add.js', {
        items: [{ id: Number(variantId), quantity: 1 }]
      });

      const updatedCart = await fetchCart();
      const updatedFillerCount = getFillerCount(updatedCart);

      if (updatedFillerCount === state.selectedCapacity) {
        if (!state.didShowCompleteToast) {
          showToast("✅ Basket complete — you've added all items!", 'complete', 3500);
          state.didShowCompleteToast = true;
        }
      } else {
        showToast('Added to basket', 'success');
      }

      await refreshProgress();
      return true;
    } catch (error) {
      console.error('[BasketBuilder] Failed to add filler item:', error);
      showToast('Unable to add item right now.', 'error');
      return false;
    }
  }

  async function refreshProgress() {
    try {
      const cart = await fetchCart();
      const fillerCount = getFillerCount(cart);
      const capacity = Number(state.selectedCapacity) || 0;
      const remaining = Math.max(capacity - fillerCount, 0);

      if (fillerCount < capacity) {
        state.didShowCompleteToast = false;
      }

      if (elements.progressHeader) {
        elements.progressHeader.textContent = `Selected ${fillerCount} of up to ${capacity} (Room for ${remaining} more)`;
      }

      if (state.currentStep === 3) {
        if (elements.reviewMeta) {
          elements.reviewMeta.textContent = `Selected ${fillerCount} of up to ${capacity} (Room for ${remaining} more)`;
        }
        if (elements.underfillNotice) {
          elements.underfillNotice.textContent = capacity > 0 && fillerCount < capacity ? `Room for ${remaining} more` : '';
        }
      }
    } catch (error) {
      console.error('[BasketBuilder] Failed to refresh progress:', error);
    }
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
    }).format(value);
  }

  function ensureVariantModalExists() {
    if (root.querySelector('#BasketBuilderVariantModal')) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div id="BasketBuilderVariantModal" class="bb-modal" hidden>
        <div class="bb-modal__backdrop" data-bb-modal-close></div>
        <div class="bb-modal__panel" role="dialog" aria-modal="true" aria-label="Choose options">
          <button type="button" class="bb-modal__close" data-bb-modal-close aria-label="Close">&times;</button>
          <div class="bb-modal__content">
            <div class="bb-modal__header">
              <img class="bb-modal__image" id="BBModalImage" alt="" />
              <div class="bb-modal__meta">
                <h3 class="bb-modal__title" id="BBModalTitle"></h3>
                <div class="bb-modal__price" id="BBModalPrice"></div>
                <div class="bb-modal__variantline" id="BBModalVariantLine"></div>
              </div>
            </div>
            <div class="bb-modal__selectors" id="BBModalSelectors"></div>
            <div class="bb-modal__actions">
              <button type="button" class="btn" id="BBModalAddBtn" disabled>Add to basket</button>
            </div>
          </div>
        </div>
      </div>
    `.trim();

    root.appendChild(wrapper.firstElementChild);
  }

  function ensureDetailsModalExists() {
    if (root.querySelector('#BasketBuilderDetailsModal')) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div id="BasketBuilderDetailsModal" class="bb-modal bb-modal--details" hidden>
        <div class="bb-modal__backdrop" data-bb-details-close></div>
        <div class="bb-modal__panel" role="dialog" aria-modal="true" aria-label="Product details">
          <button type="button" class="bb-modal__close" data-bb-details-close aria-label="Close">&times;</button>
          <div class="bb-modal__content">
            <div class="bb-modal__header bb-modal__header--details">
              <img class="bb-modal__image bb-modal__image--details" id="BBDetailsMainImage" alt="" />
              <div class="bb-modal__meta">
                <h3 class="bb-modal__title" id="BBDetailsTitle"></h3>
                <div class="bb-modal__price" id="BBDetailsPrice"></div>
              </div>
            </div>
            <div class="bb-modal__thumbs" id="BBDetailsThumbs"></div>
            <div class="bb-modal__description rte" id="BBDetailsDescription"></div>
          </div>
        </div>
      </div>
    `.trim();

    root.appendChild(wrapper.firstElementChild);
  }

  function cacheModalRefs() {
    const modal = root.querySelector('#BasketBuilderVariantModal');
    if (!modal) return null;

    modalRefs = {
      modal,
      image: modal.querySelector('#BBModalImage'),
      title: modal.querySelector('#BBModalTitle'),
      price: modal.querySelector('#BBModalPrice'),
      variantLine: modal.querySelector('#BBModalVariantLine'),
      selectors: modal.querySelector('#BBModalSelectors'),
      addBtn: modal.querySelector('#BBModalAddBtn')
    };

    return modalRefs;
  }

  function cacheDetailsModalRefs() {
    const modal = root.querySelector('#BasketBuilderDetailsModal');
    if (!modal) return null;

    detailsModalRefs = {
      modal,
      mainImage: modal.querySelector('#BBDetailsMainImage'),
      title: modal.querySelector('#BBDetailsTitle'),
      price: modal.querySelector('#BBDetailsPrice'),
      thumbs: modal.querySelector('#BBDetailsThumbs'),
      description: modal.querySelector('#BBDetailsDescription')
    };

    return detailsModalRefs;
  }

  function getAvailableModalVariants(productData) {
    if (!productData || !Array.isArray(productData.variants)) return [];
    return productData.variants.filter((variant) => variant && variant.available === true);
  }

  function findSelectedModalVariant() {
    if (!modalRefs || !currentModalProductData) return null;

    const availableVariants = getAvailableModalVariants(currentModalProductData);
    const optionNames = Array.isArray(currentModalProductData.options) ? currentModalProductData.options : [];
    const selects = Array.from(modalRefs.selectors.querySelectorAll('[data-bb-option-index]'));
    const selectedValues = optionNames.map((_, index) => {
      const select = selects.find((node) => Number(node.getAttribute('data-bb-option-index')) === index);
      return select ? select.value : '';
    });

    return availableVariants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return selectedValues.every((value, index) => variant.options[index] === value);
    }) || null;
  }

  function syncModalVariantState() {
    if (!modalRefs || !currentModalProductData) return;

    const selectedVariant = findSelectedModalVariant();
    if (selectedVariant) {
      const cents = parseInt(selectedVariant.price, 10);
      const priceText = formatMoney(Number.isFinite(cents) ? cents : 0);
      modalRefs.addBtn.disabled = false;
      modalRefs.addBtn.dataset.variantId = String(selectedVariant.id);
      modalRefs.price.textContent = priceText;
      modalRefs.variantLine.textContent = `${selectedVariant.title} — ${priceText}`;
      modalRefs.image.src = selectedVariant.image || currentModalProductData.featuredImage || '';
      modalRefs.image.alt = selectedVariant.title || currentModalProductData.title || '';
      return;
    }

    modalRefs.addBtn.disabled = true;
    delete modalRefs.addBtn.dataset.variantId;
    modalRefs.price.textContent = 'Unavailable';
    modalRefs.variantLine.textContent = '';
    modalRefs.image.src = currentModalProductData.featuredImage || '';
    modalRefs.image.alt = currentModalProductData.title || '';
  }

  function renderModalSelectors(productData) {
    if (!modalRefs) return;

    const optionNames = Array.isArray(productData.options) ? productData.options : [];
    const availableVariants = getAvailableModalVariants(productData);
    const defaultVariant = availableVariants[0] || null;

    modalRefs.selectors.innerHTML = '';

    optionNames.forEach((optionName, index) => {
      const field = document.createElement('div');
      const label = document.createElement('label');
      const select = document.createElement('select');
      const values = [];

      label.className = 'bb-modal__label';
      label.textContent = optionName;

      select.className = 'bb-modal__select';
      select.setAttribute('data-bb-option-index', String(index));

      availableVariants.forEach((variant) => {
        if (!Array.isArray(variant.options)) return;
        const value = variant.options[index];
        if (typeof value !== 'string' || values.includes(value)) return;
        values.push(value);
      });

      if (!values.length) {
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = 'Unavailable';
        select.appendChild(emptyOption);
        select.disabled = true;
      } else {
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
      }

      if (defaultVariant && Array.isArray(defaultVariant.options) && defaultVariant.options[index]) {
        select.value = defaultVariant.options[index];
      }

      select.addEventListener('change', syncModalVariantState);
      field.appendChild(label);
      field.appendChild(select);
      modalRefs.selectors.appendChild(field);
    });

    if (!optionNames.length) {
      modalRefs.price.textContent = 'Unavailable';
      modalRefs.variantLine.textContent = '';
      modalRefs.addBtn.disabled = true;
      delete modalRefs.addBtn.dataset.variantId;
      return;
    }

    syncModalVariantState();
  }

  function openVariantModal(productData, openerButton) {
    if (!modalRefs) return;

    currentModalProductData = productData;
    lastFocusedOpener = openerButton || null;

    modalRefs.title.textContent = productData && productData.title ? productData.title : '';
    modalRefs.image.src = productData && productData.featuredImage ? productData.featuredImage : '';
    modalRefs.image.alt = productData && productData.title ? productData.title : '';
    modalRefs.price.textContent = '';
    modalRefs.variantLine.textContent = '';
    modalRefs.addBtn.disabled = true;
    modalRefs.addBtn.textContent = 'Add to basket';
    delete modalRefs.addBtn.dataset.variantId;

    renderModalSelectors(productData || {});
    modalRefs.modal.hidden = false;
  }

  function closeVariantModal() {
    if (!modalRefs || modalRefs.modal.hidden) return;

    modalRefs.modal.hidden = true;
    currentModalProductData = null;

    if (lastFocusedOpener && typeof lastFocusedOpener.focus === 'function') {
      lastFocusedOpener.focus();
    }
  }

  function getProductDisplayPrice(productData) {
    if (!productData) return formatMoney(0);

    if (Array.isArray(productData.variants) && productData.variants.length) {
      const availableVariants = getAvailableModalVariants(productData);
      const firstVariant = availableVariants[0] || productData.variants[0];
      const cents = parseInt(firstVariant && firstVariant.price, 10);
      return formatMoney(Number.isFinite(cents) ? cents : 0);
    }

    const cents = parseInt(productData.price, 10);
    return formatMoney(Number.isFinite(cents) ? cents : 0);
  }

  function setDetailsMainImage(src, alt) {
    if (!detailsModalRefs || !detailsModalRefs.mainImage) return;
    detailsModalRefs.mainImage.src = src || '';
    detailsModalRefs.mainImage.alt = alt || '';
  }

  function renderDetailsThumbnails(productData) {
    if (!detailsModalRefs || !detailsModalRefs.thumbs) return;

    const fallbackImage = productData && productData.featuredImage ? [productData.featuredImage] : [];
    const images = Array.isArray(productData && productData.images) && productData.images.length
      ? productData.images
      : fallbackImage;

    detailsModalRefs.thumbs.innerHTML = '';

    if (!images.length) {
      return;
    }

    images.forEach((imageSrc, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bb-modal__thumb';
      button.setAttribute('aria-label', `Show image ${index + 1}`);
      button.dataset.imageSrc = imageSrc;

      if (index === 0) {
        button.classList.add('is-active');
      }

      button.innerHTML = `<img src="${escapeHtml(imageSrc)}" alt="" loading="lazy">`;
      button.addEventListener('click', () => {
        setDetailsMainImage(imageSrc, productData && productData.title ? productData.title : '');
        Array.from(detailsModalRefs.thumbs.querySelectorAll('.bb-modal__thumb')).forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb === button);
        });
      });

      detailsModalRefs.thumbs.appendChild(button);
    });

    setDetailsMainImage(images[0], productData && productData.title ? productData.title : '');
  }

  function openDetailsModal(productData, openerButton) {
    if (!detailsModalRefs) return;

    currentDetailsProductData = productData || null;
    lastFocusedOpener = openerButton || null;

    setDetailsMainImage('', '');
    detailsModalRefs.title.textContent = productData && productData.title ? productData.title : '';
    detailsModalRefs.price.textContent = getProductDisplayPrice(productData);
    detailsModalRefs.description.innerHTML = productData && productData.descriptionHtml
      ? productData.descriptionHtml
      : '<p>No product details available.</p>';

    renderDetailsThumbnails(productData || {});

    if (!detailsModalRefs.mainImage.getAttribute('src')) {
      setDetailsMainImage(productData && productData.featuredImage ? productData.featuredImage : '', productData && productData.title ? productData.title : '');
    }

    detailsModalRefs.modal.hidden = false;
  }

  function closeDetailsModal() {
    if (!detailsModalRefs || detailsModalRefs.modal.hidden) return;

    detailsModalRefs.modal.hidden = true;
    currentDetailsProductData = null;

    if (lastFocusedOpener && typeof lastFocusedOpener.focus === 'function') {
      lastFocusedOpener.focus();
    }
  }

  function bindVariantModalEvents() {
    if (!modalRefs || modalRefs.modal.dataset.bound === 'true') return;
    modalRefs.modal.dataset.bound = 'true';

    modalRefs.modal.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-bb-modal-close]');
      if (!closeTrigger) return;
      closeVariantModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || modalRefs.modal.hidden) return;
      closeVariantModal();
    });

    modalRefs.addBtn.addEventListener('click', async () => {
      const variantId = modalRefs.addBtn.dataset.variantId;
      if (!variantId) return;

      const originalText = modalRefs.addBtn.textContent;
      modalRefs.addBtn.disabled = true;
      modalRefs.addBtn.setAttribute('aria-busy', 'true');
      modalRefs.addBtn.textContent = 'Adding…';

      try {
        const added = await addFillerVariant(variantId);
        if (added) {
          closeVariantModal();
        }
      } finally {
        modalRefs.addBtn.removeAttribute('aria-busy');
        modalRefs.addBtn.textContent = originalText;
        if (!modalRefs.modal.hidden) {
          syncModalVariantState();
        }
      }
    });
  }

  function bindDetailsModalEvents() {
    if (!detailsModalRefs || detailsModalRefs.modal.dataset.bound === 'true') return;
    detailsModalRefs.modal.dataset.bound = 'true';

    detailsModalRefs.modal.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-bb-details-close]');
      if (!closeTrigger) return;
      closeDetailsModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || detailsModalRefs.modal.hidden) return;
      closeDetailsModal();
    });
  }

  function getTileProductData(tile) {
    if (!tile) return null;

    const jsonEl = tile.querySelector('script[data-bb-product-json]');
    if (!jsonEl) {
      console.error('[BasketBuilder] Missing product JSON for product tile.', {
        productId: tile.getAttribute('data-product-id') || 'unknown',
        productHandle: tile.getAttribute('data-product-handle') || 'unknown'
      });
      return null;
    }

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (error) {
      console.error('[BasketBuilder] Failed to parse product JSON for product tile:', error);
      showToast('Could not load product details.', 'error');
      return null;
    }
  }

  function renderReviewItems(fillerItems) {
    if (!elements.reviewItems) return;

    if (!fillerItems.length) {
      elements.reviewItems.innerHTML = '<div class="bb-review-empty">No filler items added yet.</div>';
      return;
    }

    const html = fillerItems
      .map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
          : '<div class="bb-review-thumb-placeholder" aria-hidden="true"></div>';
        return `
          <div class="bb-review-card" data-line-item-key="${item.key}">
            <div class="bb-review-thumb">${image}</div>
            <div class="bb-review-cardinfo">
              <div class="bb-review-title">${escapeHtml(item.product_title)}</div>
              <div class="bb-review-price">${formatMoney(item.final_line_price)}</div>
              <button type="button" class="bb-review-remove" data-basket-builder-remove-item data-line-item-key="${item.key}">Remove</button>
            </div>
          </div>
        `;
      })
      .join('');

    elements.reviewItems.innerHTML = `<div class="bb-review-grid">${html}</div>`;
  }

  function renderBoxSummary(boxItem) {
    if (!elements.boxSummary) return;

    if (!boxItem) {
      elements.boxSummary.innerHTML = '<p>No basket size selected.</p>';
      return;
    }

    const image = boxItem.image
      ? `<img src="${escapeHtml(boxItem.image)}" alt="${escapeHtml(boxItem.product_title)}" loading="lazy">`
      : '<div class="bb-review-thumb-placeholder" aria-hidden="true"></div>';
    const variant = boxItem.variant_title ? escapeHtml(boxItem.variant_title) : '';

    elements.boxSummary.innerHTML = `
      <div class="bb-review-box" data-box-summary>
        <div class="bb-review-thumb">${image}</div>
        <div class="bb-review-boxinfo">
          <div class="bb-review-title">${escapeHtml(boxItem.product_title)}</div>
          <div class="bb-review-subtitle">${variant}</div>
        </div>
      </div>
    `;
  }

  async function renderReview() {
    try {
      const cart = await fetchCart();
      if (elements.deliveryNotes) elements.deliveryNotes.value = cart.note || '';
      if (elements.giftNote) {
        elements.giftNote.value = (cart.attributes && (cart.attributes['Gift note'] || cart.attributes['Gift Note'])) || '';
      }
      const boxItem = getBoxItem(cart);
      const fillerItems = (cart.items || []).filter((item) => getItemIsBoxValue(item) !== true);

      renderBoxSummary(boxItem);
      renderReviewItems(fillerItems);
      await refreshProgress();
    } catch (error) {
      console.error('[BasketBuilder] Failed to render review:', error);
      showToast('Could not load basket review.', 'error');
    }
  }

  async function removeLineItemByKey(lineItemKey) {
    try {
      await postJson('/cart/change.js', { id: lineItemKey, quantity: 0 });
      state.didShowCompleteToast = false;
      await renderReview();
    } catch (error) {
      console.error('[BasketBuilder] Failed to remove line item:', error);
      showToast('Could not remove item.', 'error');
    }
  }

  async function handleCheckout(event) {
    event.preventDefault();

    const checkoutBtn = elements.checkoutBtn;
    if (checkoutBtn && checkoutBtn.getAttribute('aria-busy') === 'true') return;
    const originalText = checkoutBtn ? checkoutBtn.textContent : '';

    try {
      const cart = await fetchCart();
      const capacity = Number(state.selectedCapacity) || 0;
      const fillerCount = getFillerCount(cart);
      const remaining = Math.max(capacity - fillerCount, 0);

      if (capacity > 0 && fillerCount < capacity) {
        const ok = window.confirm(`Your basket has room for ${remaining} more item(s). Continue to checkout?`);
        if (!ok) return;
      }

      if (checkoutBtn) {
        checkoutBtn.setAttribute('aria-disabled', 'true');
        checkoutBtn.setAttribute('aria-busy', 'true');
        checkoutBtn.style.pointerEvents = 'none';
      }

      await saveCartMessages();
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[BasketBuilder] Checkout failed:', error);
      if (checkoutBtn) {
        checkoutBtn.removeAttribute('aria-disabled');
        checkoutBtn.removeAttribute('aria-busy');
        checkoutBtn.style.pointerEvents = '';
        checkoutBtn.textContent = originalText;
      }
      showToast('Could not save your notes. Please try again.', 'error');
    }
  }

  async function saveCartMessages() {
    const delivery = (elements.deliveryNotes && elements.deliveryNotes.value ? elements.deliveryNotes.value : '').trim();
    const gift = (elements.giftNote && elements.giftNote.value ? elements.giftNote.value : '').trim();

    await postJson('/cart/update.js', {
      note: delivery,
      attributes: {
        'Gift note': gift
      }
    });
  }

  async function clearAllBasketItems() {
    const cart = await fetchCart();
    for (const item of (cart.items || [])) {
      await postJson('/cart/change.js', { id: item.key, quantity: 0 });
    }
  }

  async function handleChangeBasketBox() {
    await goToStep(1);
    scrollToBuilderTop();
  }

  async function handleStartOver() {
    const confirmed = window.confirm('Start over? This will clear your basket and all added items.');
    if (!confirmed) return;

    try {
      await clearAllBasketItems();
    } catch (error) {
      console.error('[BasketBuilder] Failed to clear cart on start over:', error);
      showToast('Could not clear basket. Please try again.', 'error');
      return;
    }

    // Reset in-memory state
    state.selectedVariantId = null;
    state.selectedCapacity = 0;
    state.selectedLabel = '';
    state.selectedCategory = '';
    state.didShowCompleteToast = false;

    // Reset Step 1 UI
    elements.variantRadios.forEach((radio) => {
      radio.checked = false;
      const label = root.querySelector(`label[for="${radio.id}"]`);
      if (label) label.classList.remove('is-active');
    });

    if (elements.continueBtn) {
      elements.continueBtn.disabled = true;
    }

    if (elements.categorySelect) {
      elements.categorySelect.value = '';
    }

    if (elements.productsGrid) {
      elements.productsGrid.innerHTML = '';
    }

    currentCategoryHandle = null;
    updateStep2Header();

    // Clear persisted state then navigate to Step 1
    clearStoredState();
    setStepVisibility(1);
    scrollToBuilderTop();
  }

  function bindChangeBasketBox() {
    const buttons = root.querySelectorAll('[data-bb-change-basket]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', handleChangeBasketBox);
    });
  }

  function bindStartOver() {
    const buttons = root.querySelectorAll('[data-bb-start-over]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', handleStartOver);
    });
  }

  function bindVariantRadios() {
    elements.variantRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        setSelectedVariantFromRadio(radio);
      });
    });
  }

  function bindCategorySelect() {
    if (!elements.categorySelect) return;

    elements.categorySelect.addEventListener('change', async () => {
      const handle = elements.categorySelect.value;
      if (!handle) return;
      state.selectedCategory = handle;
      writeStoredState();
      await loadCategoryCollection(handle);
    });
  }

  function bindAddButtonsInGrid() {
    if (!elements.productsGrid) return;

    const buttons = elements.productsGrid.querySelectorAll('[data-basket-builder-add]');
    buttons.forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      button.addEventListener('click', async () => {
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;
        const originalText = button.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';

        try {
          await addFillerVariant(variantId);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
        }
      });
    });

    bindChooseOptionsButtonsInGrid();
    bindInfoButtonsInGrid();
    bindProductTilesInGrid();
    bindPaginationInGrid();

    const tiles = elements.productsGrid.querySelectorAll('.bb-card[data-product-id]');
    tiles.forEach((tile) => {
      const addButton = tile.querySelector('[data-basket-builder-add]');
      const chooseButton = tile.querySelector('[data-basket-builder-choose], [data-bb-choose-options]');
      if (addButton || chooseButton) return;

      const productId = tile.getAttribute('data-product-id') || 'unknown';
      const productHandle = tile.getAttribute('data-product-handle') || 'unknown';
      console.error('[BasketBuilder] Product tile rendered without a valid action button.', {
        productId,
        productHandle
      });
    });
  }

  function bindPaginationInGrid() {
    if (!elements.productsGrid || !currentCategoryHandle) return;

    const links = elements.productsGrid.querySelectorAll('.pagination-custom a');
    links.forEach((link) => {
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        const page = parseInt(url.searchParams.get('page'), 10) || 1;
        elements.productsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        await loadCategoryCollection(currentCategoryHandle, page);
      });
    });
  }

  function bindChooseOptionsButtonsInGrid() {
    if (!elements.productsGrid || !modalRefs) return;

    const buttons = elements.productsGrid.querySelectorAll('[data-basket-builder-choose], [data-bb-choose-options]');
    buttons.forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      button.addEventListener('click', () => {
        const tile = button.closest('[data-product-id]');
        if (!tile) return;
        const productData = getTileProductData(tile);
        if (!productData) return;
        openVariantModal(productData, button);
      });
    });
  }

  function bindInfoButtonsInGrid() {
    if (!elements.productsGrid || !detailsModalRefs) return;

    const buttons = elements.productsGrid.querySelectorAll('[data-basket-builder-info]');
    buttons.forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const tile = button.closest('[data-product-id]');
        if (!tile) return;

        const productData = getTileProductData(tile);
        if (!productData) return;
        openDetailsModal(productData, button);
      });
    });
  }

  function bindProductTilesInGrid() {
    if (!elements.productsGrid || !detailsModalRefs) return;

    const tiles = elements.productsGrid.querySelectorAll('.bb-card[data-product-id]');
    tiles.forEach((tile) => {
      if (tile.dataset.detailsBound === 'true') return;
      tile.dataset.detailsBound = 'true';

      tile.addEventListener('click', (event) => {
        if (event.target.closest('button, a, select, option, input, textarea, label')) {
          return;
        }

        const productData = getTileProductData(tile);
        if (!productData) return;
        openDetailsModal(productData, tile);
      });

      tile.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target.closest('button, a, select, option, input, textarea, label')) {
          return;
        }

        event.preventDefault();
        const productData = getTileProductData(tile);
        if (!productData) return;
        openDetailsModal(productData, tile);
      });
    });
  }

  function bindReviewEvents() {
    if (elements.reviewItems) {
      elements.reviewItems.addEventListener('click', async (event) => {
        const trigger = event.target.closest(SELECTORS.removeItemBtn);
        if (!trigger) return;
        const key = trigger.getAttribute('data-line-item-key');
        if (!key) return;
        await removeLineItemByKey(key);
      });
    }

    if (elements.reviewBtn) {
      elements.reviewBtn.addEventListener('click', async () => {
        await goToStep(3);
      });
    }

    if (elements.addMoreBtn) {
      elements.addMoreBtn.addEventListener('click', async () => {
        await goToStep(2);
      });
    }

    if (elements.checkoutBtn) {
      elements.checkoutBtn.addEventListener('click', handleCheckout);
    }
  }

  function bindPrimaryActions() {
    if (elements.continueBtn) {
      elements.continueBtn.addEventListener('click', handleContinue);
    }
  }

  function restoreStoredVariantSelection() {
    if (!state.selectedVariantId) return;

    const matching = elements.variantRadios.find(
      (radio) => Number(radio.value) === Number(state.selectedVariantId)
    );

    if (!matching) return;

    matching.checked = true;
    setSelectedVariantFromRadio(matching, false);
  }

  async function initializeDefaultStep() {
    readStoredState();
    restoreStoredVariantSelection();
    updateStep2Header();

    if (elements.continueBtn && !state.selectedVariantId) {
      elements.continueBtn.disabled = true;
    }

    // Attempt to restore a previous session if the user was past Step 1
    if (state.currentStep > 1 && state.selectedVariantId) {
      try {
        const cart = await fetchCart();
        const boxItem = getBoxItem(cart);
        if (boxItem) {
          // Restore selected category in the dropdown before navigating
          if (state.selectedCategory && elements.categorySelect) {
            elements.categorySelect.value = state.selectedCategory;
          }
          await goToStep(state.currentStep);
          return;
        }
      } catch (error) {
        console.warn('[BasketBuilder] Could not restore session, defaulting to step 1.', error);
      }
      // Cart has no box — stale state, reset to step 1 cleanly
      state.currentStep = 1;
      state.selectedCategory = '';
      clearStoredState();
    }

    setStepVisibility(1);
  }

  function exposePublicApi() {
    window.BasketBuilder = Object.assign({}, window.BasketBuilder, {
      goToStep
    });
  }

  async function init() {
    ensureVariantModalExists();
    ensureDetailsModalExists();
    cacheModalRefs();
    cacheDetailsModalRefs();
    bindVariantModalEvents();
    bindDetailsModalEvents();
    await initializeDefaultStep();
    bindVariantRadios();
    bindCategorySelect();
    bindPrimaryActions();
    bindReviewEvents();
    bindChangeBasketBox();
    bindStartOver();
    bindAddButtonsInGrid();
    exposePublicApi();
  }

  init();
})();
